/**
 * Small client-side image helpers (rotation, compression) used before
 * upload/OCR. Runs entirely in the browser; nothing is sent anywhere.
 */

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load image'));
    img.src = src;
  });
}

/**
 * Rotate an image blob by a multiple of 90 degrees. Returns the original blob
 * unchanged when rotation is 0 to preserve fidelity.
 */
export async function rotateImage(
  blob: Blob,
  degrees: 0 | 90 | 180 | 270,
): Promise<Blob> {
  if (degrees === 0) return blob;
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    const swap = degrees === 90 || degrees === 270;
    const canvas = document.createElement('canvas');
    canvas.width = swap ? img.height : img.width;
    canvas.height = swap ? img.width : img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((degrees * Math.PI) / 180);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);
    const type = blob.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const out = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, type, 0.92),
    );
    return out ?? blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export interface CompressImageOptions {
  /** Longest edge target in px (default 1800; clamp 1600–2000). */
  maxEdge?: number;
  /** JPEG quality 0–1 (default 0.85). */
  quality?: number;
}

/**
 * Downscale and re-encode for mobile upload / OCR. Keeps enough detail for
 * label text while shrinking multi‑megapixel phone captures.
 */
export async function compressImageForUpload(
  blob: Blob,
  opts: CompressImageOptions = {},
): Promise<Blob> {
  // Node / non-DOM test environments: persist the original Blob unchanged.
  if (typeof document === 'undefined' || typeof Image === 'undefined') {
    return blob;
  }
  const maxEdge = Math.min(2000, Math.max(1600, opts.maxEdge ?? 1800));
  const quality = opts.quality ?? 0.85;
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    const longest = Math.max(img.width, img.height);
    const scale = longest > maxEdge ? maxEdge / longest : 1;
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));

    // Skip re-encode when already small enough and reasonably sized.
    if (scale === 1 && blob.size < 1_200_000 && blob.type === 'image/jpeg') {
      return blob;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;
    ctx.drawImage(img, 0, 0, width, height);
    const out = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality),
    );
    return out ?? blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Convert a compressed blob into a File suitable for draft capture. */
export function blobToJpegFile(blob: Blob, name = 'capture.jpg'): File {
  return new File([blob], name, {
    type: blob.type || 'image/jpeg',
    lastModified: Date.now(),
  });
}
